import React, { useState, useEffect, useMemo, useDeferredValue } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useCart } from '@/App';
import { Search, ShoppingCart, MapPin, Clock, Phone, ChevronRight, ChevronLeft, Plus, Minus, X, History, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const LOGO_URL = "/logo-adegga.png";

const StoreFront = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { cart, addItem, getItemCount, getTotal, clearCart } = useCart();
  const [store, setStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [quickAddProduct, setQuickAddProduct] = useState(null);
  const [quickAddQty, setQuickAddQty] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [expandedCats, setExpandedCats] = useState({});
  const deferredSearch = useDeferredValue(searchTerm);
  
  // Reorder feature
  const [reorderPhone, setReorderPhone] = useState('');
  const [reorderOrders, setReorderOrders] = useState([]);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);

  useEffect(() => {
    fetchStoreData();
  }, [slug]);

  const fetchStoreData = async () => {
    try {
      const [storeRes, categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/public/stores/${slug}`),
        axios.get(`${API}/public/stores/${slug}/categories`),
        axios.get(`${API}/public/stores/${slug}/products`)
      ]);
      setStore(storeRes.data);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
    } catch (e) {
      toast.error('Loja não encontrada');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Buscar pedidos anteriores do cliente
  const fetchCustomerOrders = async () => {
    const cleanPhone = reorderPhone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      toast.error('Digite um telefone válido (DDD + número)');
      return;
    }
    
    setReorderLoading(true);
    try {
      const res = await axios.get(`${API}/public/stores/${slug}/customer-orders/${cleanPhone}`);
      setReorderOrders(res.data.orders || []);
      if (res.data.orders?.length === 0) {
        toast.info('Nenhum pedido encontrado para este telefone');
      }
    } catch (e) {
      console.error('Error fetching orders:', e);
      toast.error('Erro ao buscar pedidos');
      setReorderOrders([]);
    } finally {
      setReorderLoading(false);
    }
  };

  // Pedir de novo - adiciona todos os itens ao carrinho
  const handleReorder = (order) => {
    // Limpa o carrinho atual
    clearCart();
    
    // Adiciona cada item do pedido ao carrinho
    let itemsAdded = 0;
    order.items.forEach(item => {
      // Verifica se o produto ainda existe no catálogo
      const productExists = products.find(p => p.id === item.product_id);
      
      if (productExists) {
        addItem({
          product_id: item.product_id,
          product_name: item.name,
          quantity: item.quantity,
          unit_price: productExists.promo_price || productExists.price || item.price,
          variant_id: item.variation?.id || null,
          variant_name: item.variation?.name || null,
          addons: item.additionals || [],
          selected_options: item.options || [],
          notes: ''
        }, slug);
        itemsAdded++;
      }
    });
    
    if (itemsAdded > 0) {
      toast.success(`${itemsAdded} ${itemsAdded === 1 ? 'item adicionado' : 'itens adicionados'} ao carrinho!`);
      setReorderDialogOpen(false);
      // Navega para o checkout
      navigate(`/${slug}/checkout`);
    } else {
      toast.error('Nenhum produto deste pedido está disponível no momento');
    }
  };

  // Formatar data
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleQuickAdd = (product) => {
    if (product.variants?.length > 0 || product.addons?.length > 0 || product.option_groups?.length > 0) {
      // Se tem opções, abre o modal com seleção
      setQuickAddProduct(product);
      setQuickAddQty(1);
      // Inicializa as seleções de opções
      const initialSelections = {};
      product.option_groups?.forEach(group => {
        initialSelections[group.id] = null;
      });
      setSelectedOptions(initialSelections);
    } else {
      setQuickAddProduct(product);
      setQuickAddQty(1);
      setSelectedOptions({});
    }
  };

  const confirmQuickAdd = () => {
    if (!quickAddProduct) return;
    
    // Verificar se todas as opções obrigatórias foram selecionadas
    const hasRequiredOptions = quickAddProduct.option_groups?.some(g => g.required);
    if (hasRequiredOptions) {
      const missingRequired = quickAddProduct.option_groups?.filter(g => g.required && !selectedOptions[g.id]);
      if (missingRequired?.length > 0) {
        toast.error(`Selecione: ${missingRequired.map(g => g.name).join(', ')}`);
        return;
      }
    }
    
    // Calcular preço adicional das opções
    let priceModifier = 0;
    const selectedOptionsList = [];
    quickAddProduct.option_groups?.forEach(group => {
      const choiceId = selectedOptions[group.id];
      if (choiceId) {
        const choice = group.choices.find(c => c.id === choiceId);
        if (choice) {
          priceModifier += choice.price_modifier || 0;
          selectedOptionsList.push({
            group_name: group.name,
            choice_name: choice.name,
            price_modifier: choice.price_modifier || 0
          });
        }
      }
    });
    
    addItem({
      product_id: quickAddProduct.id,
      product_name: quickAddProduct.name,
      quantity: quickAddQty,
      unit_price: (quickAddProduct.promo_price || quickAddProduct.price) + priceModifier,
      variant_id: null,
      variant_name: null,
      addons: [],
      selected_options: selectedOptionsList,
      notes: ''
    }, slug);
    setQuickAddProduct(null);
    setSelectedOptions({});
  };

  const filteredProducts = useMemo(() => {
    const term = deferredSearch.toLowerCase();
    return products.filter(p => {
      const matchesSearch = !term || (p.name || '').toLowerCase().includes(term);
      const matchesCategory = !selectedCategory || p.category_id === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, deferredSearch, selectedCategory]);

  // Sort products: in-stock first, out-of-stock last
  const sortProductsByStock = (prods) => {
    return [...prods].sort((a, b) => {
      const aOutOfStock = a.stock_control && a.stock_quantity <= 0;
      const bOutOfStock = b.stock_control && b.stock_quantity <= 0;
      if (aOutOfStock && !bOutOfStock) return 1;
      if (!aOutOfStock && bOutOfStock) return -1;
      return 0;
    });
  };

  const groupedProducts = useMemo(() => {
    const byCat = {};
    filteredProducts.forEach(p => { (byCat[p.category_id] = byCat[p.category_id] || []).push(p); });
    return categories
      .map(cat => ({ ...cat, products: sortProductsByStock(byCat[cat.id] || []) }))
      .filter(cat => cat.products.length > 0);
  }, [categories, filteredProducts]);

  const focused = !!selectedCategory || !!deferredSearch;
  const baseLimit = focused ? 40 : 8;
  const visibleCount = (catId) => baseLimit * (expandedCats[catId] || 1);

  const isStoreOpen = () => {
    if (!store?.schedule) return true;
    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const todayIndex = now.getDay();
    const yesterdayIndex = (todayIndex + 6) % 7;
    
    const today = store.schedule[days[todayIndex]];
    const yesterday = store.schedule[days[yesterdayIndex]];
    
    const currentTime = now.getHours() * 100 + now.getMinutes();
    
    // Check if store is 24h today
    if (today?.active !== false && today?.is_24h) {
      return true;
    }
    
    // Check if yesterday's overnight shift is still active
    if (yesterday?.active !== false && yesterday?.overnight && !yesterday?.is_24h) {
      const closeTime = parseInt((yesterday.close || '22:00').replace(':', ''));
      // If close time is before 12:00 (like 03:00 = 300), check if we're before it
      if (closeTime < 1200 && currentTime < closeTime) {
        return true;
      }
    }
    
    // Normal check for today
    if (!today?.active) return false;
    
    const openTime = parseInt((today.open || '08:00').replace(':', ''));
    const closeTime = parseInt((today.close || '22:00').replace(':', ''));
    
    // If overnight, we only need to check if we're after open time
    if (today.overnight) {
      return currentTime >= openTime;
    }
    
    // Normal case: between open and close
    return currentTime >= openTime && currentTime <= closeTime;
  };

  // Get theme colors from store config
  const theme = {
    primary: store?.theme_config?.primary_color || store?.primary_color || '#ff6b00',
    secondary: store?.theme_config?.secondary_color || '#ff8533',
    background: store?.theme_config?.background_color || '#1a1a2e',
    card: store?.theme_config?.card_color || '#13131f',
    text: store?.theme_config?.text_color || '#ffffff',
    accent: store?.theme_config?.accent_color || '#ff6b00',
    headerText: store?.theme_config?.header_text_color || store?.theme_config?.text_color || '#ffffff',
    cardText: store?.theme_config?.card_text_color || store?.theme_config?.text_color || '#ffffff'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1a1a2e' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#ff6b00' }}></div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1a1a2e' }}>
        <p className="text-white">Loja não encontrada</p>
      </div>
    );
  }

  const itemCount = getItemCount();

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: theme.background }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b" style={{ backgroundColor: theme.card, borderColor: `${theme.text}10` }}>
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {store.logo_url ? (
                <img 
                  src={store.logo_url.startsWith('/') ? `${API.replace('/api', '')}${store.logo_url}` : store.logo_url} 
                  alt={store.name} 
                  className="h-14 w-14 rounded-xl object-cover shadow-lg" 
                />
              ) : (
                <div className="h-14 w-14 rounded-xl flex items-center justify-center font-bold text-xl shadow-lg" style={{ backgroundColor: theme.primary, color: theme.headerText }}>
                  {store.name.charAt(0)}
                </div>
              )}
              <div>
                <h1 className="font-bold text-lg" style={{ color: theme.headerText }} data-testid="store-name">{store.name}</h1>
                <div className="flex items-center gap-2 text-xs">
                  {isStoreOpen() ? (
                    <Badge className="bg-green-500/20 text-green-400 text-xs">Aberto</Badge>
                  ) : (
                    <Badge className="bg-red-500/20 text-red-400 text-xs">Fechado</Badge>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Reorder Button */}
              <Dialog open={reorderDialogOpen} onOpenChange={setReorderDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    style={{ color: theme.headerText }}
                    title="Pedir de novo"
                    data-testid="reorder-btn"
                  >
                    <History className="w-6 h-6" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#13131f] border-white/10 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-white">
                      <RefreshCw className="w-5 h-5 text-[#ff6b00]" />
                      Pedir de Novo
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                    <p className="text-sm text-white/60">
                      Digite seu telefone para ver seus pedidos anteriores
                    </p>
                    
                    <div className="flex gap-2">
                      <Input
                        placeholder="(31) 99999-9999"
                        value={reorderPhone}
                        onChange={(e) => setReorderPhone(e.target.value)}
                        className="bg-[#0f0f15] border-white/10 text-white"
                        maxLength={15}
                      />
                      <Button
                        onClick={fetchCustomerOrders}
                        disabled={reorderLoading || reorderPhone.replace(/\D/g, '').length < 10}
                        className="btn-primary"
                      >
                        {reorderLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    
                    {/* Orders List */}
                    {reorderOrders.length > 0 && (
                      <div className="space-y-3 max-h-[300px] overflow-y-auto">
                        <p className="text-sm text-white/60">Seus pedidos anteriores:</p>
                        {reorderOrders.map((order) => (
                          <div 
                            key={order.id} 
                            className="p-3 bg-[#0f0f15] rounded-lg border border-white/10 space-y-2"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-xs text-white/50">{formatDate(order.created_at)}</p>
                                <p className="text-sm text-white/80">
                                  {order.items_count} {order.items_count === 1 ? 'item' : 'itens'}
                                </p>
                              </div>
                              <span className="text-[#ff6b00] font-bold">
                                R$ {order.total?.toFixed(2)}
                              </span>
                            </div>
                            
                            <div className="text-xs text-white/50">
                              {order.items.slice(0, 3).map((item, i) => (
                                <span key={i}>
                                  {i > 0 && ', '}
                                  {item.quantity}x {item.name}
                                </span>
                              ))}
                              {order.items.length > 3 && <span> +{order.items.length - 3} mais</span>}
                            </div>
                            
                            <Button
                              onClick={() => handleReorder(order)}
                              size="sm"
                              className="w-full bg-green-600 hover:bg-green-700 text-white"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Pedir de Novo
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {reorderOrders.length === 0 && reorderPhone.replace(/\D/g, '').length >= 10 && !reorderLoading && (
                      <p className="text-center text-white/40 text-sm py-4">
                        Nenhum pedido encontrado para este telefone.
                      </p>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Cart Button */}
              <Link to={`/${slug}/carrinho`} className="relative" data-testid="cart-btn">
                <Button size="icon" variant="ghost" style={{ color: theme.headerText }}>
                  <ShoppingCart className="w-6 h-6" />
                </Button>
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold" style={{ backgroundColor: theme.primary, color: theme.headerText }}>
                    {itemCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Store Info */}
      <div className="border-b py-3" style={{ backgroundColor: theme.card, borderColor: `${theme.text}10` }}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-wrap gap-4 text-sm" style={{ color: `${theme.headerText}99` }}>
            {store.address?.street && (
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>
                  {store.address.street}
                  {store.address.number && `, ${store.address.number}`}
                  {store.address.neighborhood && ` - ${store.address.neighborhood}`}
                  {store.address.city && `, ${store.address.city}`}
                  {store.address.state && `/${store.address.state}`}
                </span>
              </div>
            )}
            {!store.address?.street && store.address?.neighborhood && (
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{store.address.neighborhood}, {store.address.city}</span>
              </div>
            )}
            {store.whatsapp && (
              <div className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                <span>{store.whatsapp}</span>
              </div>
            )}
            {store.delivery_enabled && store.delivery_fee?.type === 'fixed' && store.delivery_fee?.fixed_value > 0 && (
              <div className="flex items-center gap-1" style={{ color: theme.primary }}>
                <span>Entrega: R$ {store.delivery_fee?.fixed_value?.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: `${theme.text}60` }} />
          <Input
            placeholder="Buscar produtos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border"
            style={{ backgroundColor: theme.card, borderColor: `${theme.text}20`, color: theme.text }}
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Categories Filter - Improved Navigation */}
      <div className="max-w-4xl mx-auto px-4 mb-4">
        <div className="relative group">
          {/* Left Arrow - Desktop only */}
          <button
            onClick={() => {
              const container = document.getElementById('categories-scroll');
              if (container) container.scrollBy({ left: -200, behavior: 'smooth' });
            }}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ backgroundColor: theme.card, color: theme.text, border: `1px solid ${theme.text}20` }}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          {/* Scrollable Categories */}
          <div 
            id="categories-scroll"
            className="flex gap-2 overflow-x-auto scrollbar-hide scroll-smooth px-1 py-1"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <Button
              size="sm"
              onClick={() => setSelectedCategory(null)}
              className="flex-shrink-0"
              style={selectedCategory === null 
                ? { backgroundColor: theme.primary, color: theme.text }
                : { backgroundColor: `${theme.text}15`, color: theme.text }
              }
            >
              Todos
            </Button>
            {categories.map(cat => (
              <Button
                key={cat.id}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className="flex-shrink-0"
                style={selectedCategory === cat.id 
                  ? { backgroundColor: theme.primary, color: theme.text }
                  : { backgroundColor: `${theme.text}15`, color: theme.text }
                }
              >
                {cat.name}
              </Button>
            ))}
          </div>
          
          {/* Right Arrow - Desktop only */}
          <button
            onClick={() => {
              const container = document.getElementById('categories-scroll');
              if (container) container.scrollBy({ left: 200, behavior: 'smooth' });
            }}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ backgroundColor: theme.card, color: theme.text, border: `1px solid ${theme.text}20` }}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          
          {/* Gradient fade indicators */}
          <div 
            className="hidden md:block absolute left-0 top-0 bottom-0 w-8 pointer-events-none"
            style={{ background: `linear-gradient(to right, ${theme.background}, transparent)` }}
          />
          <div 
            className="hidden md:block absolute right-0 top-0 bottom-0 w-8 pointer-events-none"
            style={{ background: `linear-gradient(to left, ${theme.background}, transparent)` }}
          />
        </div>
      </div>

      {/* Products */}
      <div className="max-w-4xl mx-auto px-4 space-y-8">
        {groupedProducts.map(category => (
          <div key={category.id} className="animate-fade-in">
            <h2 className="text-lg font-bold mb-3" style={{ color: theme.text }} data-testid={`category-${category.id}`}>
              {category.name}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {category.products.slice(0, visibleCount(category.id)).map(product => (
                <div
                  key={product.id}
                  className="p-3 flex gap-3 cursor-pointer rounded-lg border"
                  style={{ backgroundColor: theme.card, borderColor: `${theme.text}10` }}
                  onClick={() => handleQuickAdd(product)}
                  data-testid={`product-${product.id}`}
                >
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                      className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${theme.cardText}10` }}>
                      <span className="text-2xl" style={{ opacity: 0.3 }}>🍺</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate" style={{ color: theme.cardText }}>{product.name}</h3>
                    {product.description && (
                      <p className="text-sm line-clamp-2" style={{ color: `${theme.cardText}80` }}>{product.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      {product.promo_price ? (
                        <>
                          <span className="line-through text-sm" style={{ color: `${theme.cardText}50` }}>R$ {product.price.toFixed(2)}</span>
                          <span className="font-bold" style={{ color: theme.primary }}>R$ {product.promo_price.toFixed(2)}</span>
                        </>
                      ) : (
                        <span className="font-bold" style={{ color: theme.primary }}>R$ {product.price.toFixed(2)}</span>
                      )}
                    </div>
                    {product.stock_control && product.stock_quantity <= 0 && (
                      <Badge className="bg-red-500/20 text-red-400 text-xs mt-1">Esgotado</Badge>
                    )}
                  </div>
                  {/* Hide add button for out of stock products */}
                  {(!product.stock_control || product.stock_quantity > 0) && (
                    <Button
                      size="icon"
                      className="h-8 w-8 flex-shrink-0 self-end"
                      style={{ backgroundColor: theme.primary }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuickAdd(product);
                      }}
                    >
                      <Plus className="w-4 h-4" style={{ color: theme.cardText }} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {category.products.length > visibleCount(category.id) && (
              <button
                onClick={() => setExpandedCats(prev => ({ ...prev, [category.id]: (prev[category.id] || 1) + (focused ? 1 : 5) }))}
                className="w-full mt-3 py-2 rounded-lg text-sm font-medium border transition-opacity hover:opacity-80"
                style={{ color: theme.primary, borderColor: `${theme.primary}40`, backgroundColor: `${theme.primary}10` }}
                data-testid={`category-show-more-${category.id}`}
              >
                Ver mais {category.products.length - visibleCount(category.id)} produtos
              </button>
            )}
          </div>
        ))}

        {filteredProducts.length === 0 && (
          <div className="text-center py-12" style={{ color: `${theme.text}80` }}>
            <p>Nenhum produto encontrado</p>
          </div>
        )}
      </div>

      {/* Quick Add Modal */}
      <Sheet open={!!quickAddProduct} onOpenChange={() => setQuickAddProduct(null)}>
        <SheetContent side="bottom" className="rounded-t-xl" style={{ backgroundColor: theme.card, borderColor: `${theme.text}10` }}>
          {quickAddProduct && (
            <div className="py-4">
              <div className="flex items-center gap-4 mb-6">
                {quickAddProduct.images?.[0] ? (
                  <img src={quickAddProduct.images[0]} alt="" className="w-16 h-16 rounded-lg object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.text}10` }}>
                    <span className="text-2xl">🍺</span>
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-lg" style={{ color: theme.text }}>{quickAddProduct.name}</h3>
                  <span className="font-bold" style={{ color: theme.primary }}>
                    R$ {(quickAddProduct.promo_price || quickAddProduct.price).toFixed(2)}
                  </span>
                </div>
              </div>
              
              {/* Option Groups */}
              {quickAddProduct.option_groups?.length > 0 && (
                <div className="space-y-4 mb-6">
                  {quickAddProduct.option_groups.map(group => (
                    <div key={group.id}>
                      <label className="text-sm font-medium mb-2 block" style={{ color: theme.text }}>
                        {group.name}
                        {group.required && <span style={{ color: theme.primary }}> *</span>}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {group.choices.map(choice => (
                          <button
                            key={choice.id}
                            onClick={() => setSelectedOptions({ ...selectedOptions, [group.id]: choice.id })}
                            className="px-3 py-2 rounded-lg text-sm border-2 transition-all"
                            style={{
                              backgroundColor: selectedOptions[group.id] === choice.id ? `${theme.primary}20` : `${theme.text}08`,
                              borderColor: selectedOptions[group.id] === choice.id ? theme.primary : 'transparent',
                              color: theme.text
                            }}
                          >
                            {choice.name}
                            {choice.price_modifier > 0 && (
                              <span className="ml-1" style={{ color: theme.primary }}>
                                +R$ {choice.price_modifier.toFixed(2)}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex items-center justify-center gap-4 mb-6">
                <Button
                  size="icon"
                  variant="secondary"
                  onClick={() => setQuickAddQty(Math.max(1, quickAddQty - 1))}
                  style={{ backgroundColor: `${theme.text}15`, color: theme.text }}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-2xl font-bold w-12 text-center" style={{ color: theme.text }}>{quickAddQty}</span>
                <Button
                  size="icon"
                  variant="secondary"
                  onClick={() => setQuickAddQty(quickAddQty + 1)}
                  style={{ backgroundColor: `${theme.text}15`, color: theme.text }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Show out of stock message or add button */}
              {quickAddProduct.stock_control && quickAddProduct.stock_quantity <= 0 ? (
                <div className="w-full h-12 flex items-center justify-center bg-red-500/20 rounded-lg">
                  <span className="text-red-400 font-medium">Produto Esgotado</span>
                </div>
              ) : (
                <Button
                  className="w-full h-12 text-lg"
                  style={{ backgroundColor: theme.primary, color: theme.text }}
                  onClick={confirmQuickAdd}
                  data-testid="confirm-add-btn"
                >
                  Adicionar R$ {(() => {
                    let total = (quickAddProduct.promo_price || quickAddProduct.price);
                    quickAddProduct.option_groups?.forEach(group => {
                      const choiceId = selectedOptions[group.id];
                      if (choiceId) {
                        const choice = group.choices.find(c => c.id === choiceId);
                        if (choice) total += choice.price_modifier || 0;
                      }
                    });
                    return (total * quickAddQty).toFixed(2);
                  })()}
                </Button>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Floating Cart Button */}
      {itemCount > 0 && (
        <div className="fixed bottom-6 left-4 right-4 max-w-4xl mx-auto z-40">
          <Link to={`/${slug}/carrinho`}>
            <Button 
              className="w-full h-14 text-lg justify-between px-6" 
              style={{ backgroundColor: theme.primary, color: theme.text }}
              data-testid="view-cart-btn"
            >
              <span className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Ver Carrinho ({itemCount})
              </span>
              <span>R$ {getTotal().toFixed(2)}</span>
            </Button>
          </Link>
        </div>
      )}

      {/* Floating WhatsApp Button */}
      {store.whatsapp && (
        <a
          href={`https://wa.me/55${store.whatsapp.replace(/\D/g, '')}?text=Olá! Vim pelo cardápio digital.`}
          target="_blank"
          rel="noopener noreferrer"
          className={`fixed z-50 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20BA5C] text-white flex items-center justify-center shadow-lg transition-all hover:scale-110 ${itemCount > 0 ? 'bottom-24' : 'bottom-6'} right-4`}
          data-testid="whatsapp-float-btn"
          aria-label="Falar no WhatsApp"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      )}
    </div>
  );
};

export default StoreFront;
